import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Collapse,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Sidebar from '../../Pages/landingPage/Sidebar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import EditIcon from '../../Images/Edit.png';
import { getOrganizations } from '../../apis/Admin';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import OrganizationDialog from '../OrganizationDialog';
import { AdminTabs } from '../AdminTabs';
import { useTimezone } from '../../hooks/useTimezone';
import {
  Add,
  Person4,
  SettingsRounded,
  TrackChanges,
  Announcement,
  Business,
  ContactMail,
  RateReview,
} from '@mui/icons-material';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, type: 'spring', stiffness: 100 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.3, ease: 'easeOut' },
  }),
};

export default function AdminOrganizations() {
  const navigate = useNavigate();
  const { formatDate } = useTimezone();

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'create',
    organization: null,
  });

  const [pageError, setPageError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  // Super admin check
  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/admin/user', { replace: true });
    }
  }, [isSuperAdmin, navigate]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const orgsData = await getOrganizations();
      setOrganizations(Array.isArray(orgsData) ? orgsData : []);
      setPageError(null);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setPageError('Error fetching organization data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.column === column && sortConfig.direction === 'asc')
      direction = 'desc';
    else if (sortConfig.column === column && sortConfig.direction === 'desc') {
      direction = 'none';
      column = null;
    }
    setSortConfig({ column, direction });
  };

  const applySort = (rows) => {
    if (sortConfig.direction === 'none' || !sortConfig.column) return rows;
    const arr = [...rows];
    return arr.sort((a, b) => {
      let A, B;
      switch (sortConfig.column) {
        case 'org_name':
          A = (a.org_name || '').toLowerCase();
          B = (b.org_name || '').toLowerCase();
          break;
        case 'total_seats':
          A = a.total_seats || 0;
          B = b.total_seats || 0;
          break;
        case 'used_seats':
          A = a.used_seats || 0;
          B = b.used_seats || 0;
          break;
        case 'status':
          A = a.is_active ? 'active' : 'inactive';
          B = b.is_active ? 'active' : 'inactive';
          break;
        case 'created_at':
          A = a.created_at || '';
          B = b.created_at || '';
          break;
        case 'trial_status': {
          const aPaid = a.is_paid ? 0 : (a.trial_end_date ? (new Date(a.trial_end_date) > new Date() ? 1 : 2) : 3);
          const bPaid = b.is_paid ? 0 : (b.trial_end_date ? (new Date(b.trial_end_date) > new Date() ? 1 : 2) : 3);
          A = aPaid;
          B = bPaid;
          break;
        }
        default:
          return 0;
      }
      if (sortConfig.column === 'total_seats' || sortConfig.column === 'used_seats' || sortConfig.column === 'created_at' || sortConfig.column === 'trial_status') {
        return sortConfig.direction === 'asc' ? A - B : B - A;
      }
      return sortConfig.direction === 'asc'
        ? A.localeCompare(B)
        : B.localeCompare(A);
    });
  };

  const filtered = useMemo(() => {
    const matchesQuery = (org) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;

      const orgName = (org.org_name || '').toLowerCase();
      const description = (org.description || '').toLowerCase();

      return orgName.includes(q) || description.includes(q);
    };

    const rows = organizations.filter(matchesQuery);
    return applySort(rows);
  }, [organizations, search, sortConfig]);

  const displayed = showAll ? filtered : filtered.slice(0, 10);

  const Pill = ({ children, color }) => (
    <Box
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.35,
        borderRadius: '10px',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        color,
        bgcolor: alpha(color, 0.12),
        textTransform: 'capitalize',
      }}
    >
      {children}
    </Box>
  );

  Pill.propTypes = {
    children: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired,
  };

  const getSeatColor = (availableSeats, totalSeats) => {
    if (totalSeats === 0) return '#6b7280'; // Unlimited - gray
    if (availableSeats > 0) return '#2e7d32'; // Green
    if (availableSeats === 0) return '#ed6c02'; // Yellow
    return '#d32f2f'; // Red (over limit)
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          bgcolor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        <Sidebar />
        <Box sx={{ flexGrow: 1, px: 3, py: 2 }}>
          <Collapse in={!!pageError} unmountOnExit>
            <Alert
              severity="error"
              onClose={() => setPageError(null)}
              sx={{
                mb: 2,
                border: '1px solid #fecaca',
                background: '#fee2e2',
                color: '#7f1d1d',
              }}
            >
              {pageError}
            </Alert>
          </Collapse>

          <AdminTabs
            items={[
              {
                to: '/admin/organizations',
                label: 'Organizations',
                icon: Business,
              },
              { to: '/admin/user', label: 'User Management', icon: Person4 },
              {
                to: '/admin/annoucement',
                label: 'Annoucement',
                icon: Announcement,
              },
              {
                to: '/admin/setting',
                label: 'Application Settings',
                icon: SettingsRounded,
              },
              {
                to: '/admin/audit-log',
                label: 'Audit Logs',
                icon: TrackChanges,
              },
              {
                to: '/admin/contact-submissions',
                label: 'Contact Submissions',
                icon: ContactMail,
              },
              ...(isSuperAdmin
                ? [
                    {
                      to: '/admin/public-reviews',
                      label: 'Public Reviews',
                      icon: RateReview,
                    },
                  ]
                : []),
            ]}
          />

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Box
              component={motion.div}
              sx={{
                borderRadius: '4px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                p: 0,
                mt: 1,
                mb: 3,
                position: 'relative',
                overflow: 'hidden',
                boxShadow:
                  '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
              }}
            >
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2.5,
                    borderBottom: '1px solid #e2e8f0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        component="h2"
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.1rem' },
                          fontWeight: 600,
                          color: '#111827',
                          letterSpacing: '0.03em',
                          lineHeight: 1.2,
                        }}
                      >
                        Organizations
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AnimatePresence initial={false}>
                      {searchOpen ? (
                        <motion.div
                          key="searchbar"
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: 280, opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={{ type: 'tween', duration: 0.25 }}
                        >
                          <TextField
                            autoFocus
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search organizations…"
                            size="small"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon
                                    fontSize="small"
                                    sx={{ color: '#6B7280' }}
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSearch('');
                                      setSearchOpen(false);
                                    }}
                                    edge="end"
                                  >
                                    <CloseRounded sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: 34,
                                borderRadius: '8px',
                                background: '#F8FAFC',
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="searchicon"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <Tooltip title="Search organizations">
                            <IconButton
                              onClick={() => setSearchOpen(true)}
                              size="small"
                              sx={{
                                border: '1px solid rgba(79, 172, 254, 0.2)',
                                background: '#EEF2FF',
                                color: '#5965A6',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                transition:
                                  'transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .2s ease, background-color .2s ease',
                                '&:hover': {
                                  background: '#E5E9FF',
                                  transform: 'translateY(-1px) scale(1.02)',
                                  boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                                },
                              }}
                            >
                              <SearchIcon />
                            </IconButton>
                          </Tooltip>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {isSuperAdmin && (
                      <motion.div whileTap={{ scale: 0.98 }}>
                        <Button
                          variant="contained"
                          onClick={() =>
                            setDialog({ open: true, mode: 'create', organization: null })
                          }
                          startIcon={
                            <Add
                              sx={{
                                fontSize: 20,
                                fontWeight: 600,
                                stroke: '#3A4DA8',
                                strokeWidth: 0.5,
                              }}
                            />
                          }
                          sx={{
                            fontSize: '0.875rem',
                            backgroundColor: '#5965A6',
                            padding: '6px 14px',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '6px',
                            fontWeight: 500,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            '&:hover': {
                              backgroundColor: '#4C5B9A',
                              transform: 'translateY(-1px) scale(1.02)',
                              boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                            },
                          }}
                        >
                          Create Organization
                        </Button>
                      </motion.div>
                    )}

                    {filtered.length > 10 && (
                      <motion.div
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowAll(!showAll)}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#5965A6',
                            cursor: 'pointer',
                            px: 2,
                            py: 1,
                            borderRadius: '8px',
                            border: '1px solid rgba(79, 172, 254, 0.2)',
                            background: '#EEF2FF',
                            userSelect: 'none',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            '&:hover': {
                              boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                            },
                          }}
                        >
                          {showAll
                            ? 'View Less'
                            : `View All (${filtered.length})`}
                        </Typography>
                      </motion.div>
                    )}

                    {loading && (
                      <CircularProgress size={24} sx={{ color: '#5965A6' }} />
                    )}
                  </Box>
                </Box>
              </motion.div>

              <TableContainer
                sx={{ overflow: 'hidden', paddingBottom: '1rem' }}
              >
                <Table
                  size="small"
                  sx={{
                    width: '100%',
                    '& .MuiTableCell-root': {
                      py: 0.5,
                      px: 2,
                      borderBottom: '1px solid #e5e7eb',
                    },
                    '& thead .MuiTableCell-root': { py: 0.5, px: 1.25 },
                    '& .MuiTableRow-root': { height: 44 },
                  }}
                >
                  <TableHead sx={{ backgroundColor: '#5965A6' }}>
                    <TableRow
                      sx={{
                        '&:hover': { backgroundColor: '#4C5B9A' },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell sx={thSx}>Sr No</TableCell>
                      {[
                        { label: 'Organization Name', key: 'org_name' },
                        { label: 'Description', key: 'description' },
                        { label: 'Trial Status', key: 'trial_status' },
                        { label: 'Total Seats', key: 'total_seats' },
                        { label: 'Used Seats', key: 'used_seats' },
                        { label: 'Available Seats', key: 'available_seats' },
                        { label: 'Status', key: 'status' },
                        { label: 'Created Date', key: 'created_at' },
                      ].map((h) => (
                        <TableCell
                          key={h.key}
                          onClick={() => handleSort(h.key)}
                          sx={thSxClickable}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            {h.label}
                            {sortConfig.column === h.key &&
                              sortConfig.direction === 'asc' && (
                                <ArrowUpwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {sortConfig.column === h.key &&
                              sortConfig.direction === 'desc' && (
                                <ArrowDownwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {(!sortConfig.column ||
                              sortConfig.column !== h.key) && (
                              <UnfoldMoreIcon
                                sx={{
                                  fontSize: '0.875rem',
                                  ml: 0.5,
                                  opacity: 0.5,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      ))}
                      <TableCell sx={thSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {displayed.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Typography
                              sx={{
                                color: '#64748b',
                                fontSize: '0.95rem',
                                mb: 1,
                              }}
                            >
                              No organizations found.
                            </Typography>
                            {isSuperAdmin && (
                              <Typography
                                sx={{ color: '#94a3b8', fontSize: '0.85rem' }}
                              >
                                Click &quot;Create Organization&quot; to create
                                one.
                              </Typography>
                            )}
                          </motion.div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {displayed.map((org, i) => {
                          const availableSeats =
                            org.available_seats !== undefined
                              ? org.available_seats
                              : org.total_seats === 0
                                ? -1
                                : (org.total_seats || 0) - (org.used_seats || 0);
                          const seatColor = getSeatColor(
                            availableSeats,
                            org.total_seats || 0,
                          );

                          return (
                            <TableRow
                              key={org.org_id}
                              component={motion.tr}
                              custom={i}
                              variants={rowVariants}
                              initial="hidden"
                              animate="visible"
                              sx={{
                                borderBottom: '1px solid #F3F4F6',
                                backgroundColor:
                                  i % 2 === 1 ? '#F3F5F7' : '#ffffff',
                                '&:hover': {
                                  backgroundColor: '#71B9FF40',
                                },
                              }}
                            >
                              <TableCell sx={tdIndexSx}>{i + 1}</TableCell>
                              <TableCell sx={tdTextSx}>
                                {org.org_name || '—'}
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 0.25,
                                  px: 2,
                                  fontSize: '0.875rem',
                                  color: '#6b7280',
                                  maxWidth: 200,
                                }}
                              >
                                <Typography
                                  noWrap
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: '#6b7280',
                                  }}
                                  title={org.description || ''}
                                >
                                  {org.description || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                {org.is_paid ? (
                                  <Pill color="#2e7d32">Paid</Pill>
                                ) : !org.is_active && !org.is_paid ? (
                                  <Pill color="#d32f2f">Expired</Pill>
                                ) : org.trial_end_date ? (
                                  (() => {
                                    const endDate = new Date(org.trial_end_date);
                                    const now = new Date();
                                    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                                    const color = daysRemaining <= 4 ? '#ed6c02' : daysRemaining <= 0 ? '#d32f2f' : '#2e7d32';
                                    return (
                                      <Pill color={color}>
                                        {daysRemaining <= 0 ? 'Expired' : `${daysRemaining} days left`}
                                      </Pill>
                                    );
                                  })()
                                ) : (
                                  <Pill color="#6b7280">No Trial</Pill>
                                )}
                              </TableCell>
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                {org.total_seats === 0 ? (
                                  <Typography
                                    sx={{
                                      fontSize: '0.875rem',
                                      color: '#6b7280',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    Unlimited
                                  </Typography>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontSize: '0.875rem',
                                      color: '#6b7280',
                                    }}
                                  >
                                    {org.total_seats || 0}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                <Typography
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: '#6b7280',
                                  }}
                                >
                                  {org.used_seats || 0}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                <Typography
                                  sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: seatColor,
                                  }}
                                >
                                  {availableSeats === -1
                                    ? 'Unlimited'
                                    : availableSeats}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                <Pill
                                  color={
                                    org.is_active === false
                                      ? '#ed6c02'
                                      : '#2e7d32'
                                  }
                                >
                                  {org.is_active === false
                                    ? 'Inactive'
                                    : 'Active'}
                                </Pill>
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 0.25,
                                  px: 2,
                                  fontSize: '0.875rem',
                                  color: '#6b7280',
                                }}
                              >
                                {org.created_at
                                  ? formatDate(org.created_at, {
                                      includeTime: true,
                                    })
                                  : 'N/A'}
                              </TableCell>
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {isSuperAdmin && (
                                    <Tooltip title="Edit Organization">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDialog({
                                            open: true,
                                            mode: 'edit',
                                            organization: org,
                                          });
                                        }}
                                        sx={{
                                          color: '#6b7280',
                                          p: '6px',
                                          borderRadius: '6px',
                                          '&:hover': {
                                            backgroundColor: '#f3f4f6',
                                          },
                                        }}
                                      >
                                        <img
                                          src={EditIcon}
                                          alt="Edit"
                                          style={{ width: 22, height: 22 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </AnimatePresence>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </motion.div>
        </Box>
      </Box>

      <OrganizationDialog
        open={dialog.open}
        mode={dialog.mode}
        organization={dialog.organization}
        onClose={() =>
          setDialog({ open: false, mode: 'create', organization: null })
        }
        onSubmit={async () => {
          await fetchOrganizations();
        }}
      />
    </>
  );
}

const thSx = {
  fontSize: '0.75rem',
  fontWeight: 500,
  py: 1,
  px: 2,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #E5E7EB',
};

const thSxClickable = {
  ...thSx,
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    transition: 'background-color 0.2s ease',
  },
};

const tdIndexSx = {
  py: 0.25,
  px: 2,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#6b7280',
  verticalAlign: 'middle',
};

const tdTextSx = () => ({ py: 0.25, px: 2 });

